import { EntityInfoToSub } from '@energybox/react-ui-library/dist/types/StreamApi';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Channel,
  subscribeToDeviceStatus,
  unsubscribeFromDeviceStatus,
} from '../../actions/streamApi';
import { CHANNEL_CONFIG } from '../../config/websocketChannels';

/**
 * The authority on topic identity, so this hook and the STOMP registry cannot
 * disagree about what counts as the same subscription.
 */
const topicKey = CHANNEL_CONFIG[Channel.DEVICE_STATUS]!.getSubscriptionKey;

/**
 * Cheap enough to run on every render of every row that renders a device
 * status: one string, no Map and no sort. Reordering the same devices yields a
 * different signature, which costs one extra effect run that then diffs to
 * nothing -- much cheaper than sorting on every render to avoid it.
 */
const topicSignature = (entities: EntityInfoToSub[] | undefined) => {
  if (!entities || !entities.length) {
    return '';
  }
  let signature = '';
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    if (entity && entity.uuid && entity.vendor) {
      signature += `${topicKey(entity)}|`;
    }
  }
  return signature;
};

const toTopicMap = (entities: EntityInfoToSub[] | undefined) => {
  const map = new Map<string, EntityInfoToSub>();
  (entities || []).forEach(entity => {
    if (entity && entity.uuid && entity.vendor) {
      map.set(topicKey(entity), entity);
    }
  });
  return map;
};

/**
 * Subscribes to DEVICE_STATUS for a set of devices, in balance: every subscribe
 * this hook dispatches is matched by exactly one unsubscribe, whatever the
 * device set does in between. Changing the set only subscribes what was added
 * and unsubscribes what was removed.
 *
 * That balance is what lets the STOMP layer reference count shared topics, so a
 * device watched by two mounted components keeps streaming when one of them
 * unmounts. The library's `useSubscribeToEntities` cannot offer it: it re-issues
 * subscribes for devices it is already watching whenever the list grows, and
 * skips the unsubscribe entirely if the socket happens to be down at unmount.
 *
 * There is also no `streamConnected` gate. The connection manager records the
 * intent while offline and restores it on connect.
 */
const useSubscribeToDeviceStatuses = (
  entities: EntityInfoToSub[] | undefined
) => {
  const dispatch = useDispatch();
  // Re-runs only when the topic set really changes, not on every render.
  const signature = topicSignature(entities);
  const entitiesRef = useRef(entities);
  entitiesRef.current = entities;
  const subscribed = useRef(new Map<string, EntityInfoToSub>());

  useEffect(() => {
    // Built here rather than per render: most callers of this hook render on
    // every inbound status batch and never change their device set.
    const next = toTopicMap(entitiesRef.current);
    const current = subscribed.current;

    current.forEach((entity, key) => {
      if (!next.has(key)) {
        dispatch(
          unsubscribeFromDeviceStatus(entity.vendor, entity.uuid, entity.id)
        );
      }
    });
    next.forEach((entity, key) => {
      if (!current.has(key)) {
        dispatch(
          subscribeToDeviceStatus(entity.vendor, entity.uuid, entity.id)
        );
      }
    });

    subscribed.current = next;
  }, [dispatch, signature]);

  // Separate effect so teardown runs once, on unmount, and releases whatever is
  // still held rather than whatever the last render happened to ask for.
  useEffect(
    () => () => {
      subscribed.current.forEach(entity => {
        dispatch(
          unsubscribeFromDeviceStatus(entity.vendor, entity.uuid, entity.id)
        );
      });
      subscribed.current = new Map();
    },
    [dispatch]
  );
};

export default useSubscribeToDeviceStatuses;
